import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { queryOptions, useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import {
  getMetaPixelIntegration,
  removeMetaPixelIntegration,
  updateMetaPixelIntegration,
  type MetaPixelOrganizationScope,
} from './meta-pixel.api'
import type { MetaPixelIntegration, MetaPixelIntegrationInput } from './meta-pixel.types'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

/** The key entries the fetcher reads; the scoped key builder below sets them. */
type MetaPixelIntegrationQueryKey = [AdminQueryRoot & MetaPixelOrganizationScope]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const metaPixelQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'metaPixel' }, { organizationId }),
  ],
  integration: ({ organizationId }: OrganizationScope = {}): MetaPixelIntegrationQueryKey =>
    buildScopedAdminQueryKey(metaPixelQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'integration',
    }),
}

export const metaPixelQueries = {
  integration: ({ organizationId }: OrganizationScope = {}) =>
    queryOptions({
      queryKey: metaPixelQueryKeys.integration({ organizationId }),
      queryFn: getMetaPixelIntegration,
    }),
}

type MetaPixelIntegrationParams = OrganizationScope &
  QueryParams<ReturnType<typeof metaPixelQueries.integration>>

function syncIntegration(organizationId: number, integration: MetaPixelIntegration) {
  queryClient.setQueryData(metaPixelQueryKeys.integration({ organizationId }), integration)
}

export function useMetaPixelIntegration({
  organizationId,
  ...params
}: MetaPixelIntegrationParams = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...metaPixelQueries.integration({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

export function useSuspenseMetaPixelIntegration({
  organizationId,
  ...params
}: MetaPixelIntegrationParams = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...metaPixelQueries.integration({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

export function useUpdateMetaPixelIntegrationMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: MetaPixelIntegrationInput) =>
      updateMetaPixelIntegration({ organizationId, ...values }),
    onSuccess: (integration) => syncIntegration(organizationId, integration),
  })
}

export function useRemoveMetaPixelIntegrationMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: () => removeMetaPixelIntegration({ organizationId }),
    onSuccess: (integration) => syncIntegration(organizationId, integration),
  })
}
